import type LaunchDarkly from "@packages/types/launch-darkly";
import type { LeadSubmissionResponse } from "@packages/types/front-end-api/lead-submission";
import type Paths from "@packages/types/front-end-api/v1";
import type { FormikValues } from "formik";

const CLIENT_ACTIONS_API_PATH = "/api/client-actions";

type PostLeadSubmissionData =
	| Paths.paths["/api/v1/lead-submission"]["post"]["responses"][200]["content"]["application/json"]
	| Paths.paths["/api/v1/lead-submission"]["post"]["responses"][201]["content"]["application/json"];
type PostSecondaryOffersSubmissionStepData =
	Paths.paths["/api/v1/secondary-offer-submission"]["post"]["responses"][200]["content"]["application/json"];
type PatchSecondaryOffersSubmissionData =
	| Paths.paths["/api/v1/secondary-offer-submission"]["patch"]["responses"][200]["content"]["application/json"]
	| Paths.paths["/api/v1/secondary-offer-submission"]["patch"]["responses"][201]["content"]["application/json"];
type PostSecondaryOffersSubmissionFormData =
	Paths.paths["/api/v1/secondary-offer-submission/submit"]["post"]["responses"][201]["content"]["application/json"];
type GetSecondaryOffersSubmissionData =
	Paths.paths["/api/v1/secondary-offer-submission"]["get"]["responses"][200]["content"]["application/json"];

async function callClientAction<T>(body: object): Promise<T> {
	const response = await fetch(CLIENT_ACTIONS_API_PATH, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});

	if (!response.ok) {
		throw new Error(`Client action request failed: ${response.status}`);
	}

	return (await response.json()) as T;
}

export async function postEvent(name: string, meta: unknown) {
	try {
		return await callClientAction<{ data?: unknown; error?: string }>({
			action: "postEvent",
			name,
			meta,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function validateEmail(
	emailAddress: string,
): Promise<boolean | undefined> {
	try {
		const { value } = await callClientAction<{ value?: boolean }>({
			action: "validateEmail",
			emailAddress,
		});

		return value;
	} catch {
		return undefined;
	}
}

export async function validatePhoneNumber(
	phoneNumber: string,
): Promise<{ validPhoneNumber: boolean | undefined; timedOut?: boolean }> {
	try {
		return await callClientAction<{
			validPhoneNumber: boolean | undefined;
			timedOut?: boolean;
		}>({
			action: "validatePhoneNumber",
			phoneNumber,
		});
	} catch {
		return { validPhoneNumber: undefined };
	}
}

export async function patchLeadSubmissionDraft(formData: FormikValues) {
	try {
		return await callClientAction<{ data?: unknown; error?: string }>({
			action: "patchLeadSubmissionDraft",
			formData,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function postTrustedForm(certUrl: string, pingUrl: string) {
	try {
		return await callClientAction<{ data?: unknown; error?: string }>({
			action: "postTrustedForm",
			certUrl,
			pingUrl,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function postLeadSubmission(formData: FormikValues) {
	try {
		return await callClientAction<{
			data?: PostLeadSubmissionData;
			error?: string;
		}>({
			action: "postLeadSubmission",
			formData,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function getLeadSubmission() {
	try {
		return await callClientAction<{
			data?: LeadSubmissionResponse;
			error?: string;
		}>({
			action: "getLeadSubmission",
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function getSecondaryOfferFlowEnabled(
	additionalContext?: LaunchDarkly.AdditionalContext,
) {
	try {
		return await callClientAction<boolean>({
			action: "getSecondaryOfferFlowEnabled",
			additionalContext,
		});
	} catch {
		return false;
	}
}

export async function postSecondaryOffersSubmissionStep(
	submissionType: string,
	submissionData: unknown,
) {
	try {
		return await callClientAction<{
			data?: PostSecondaryOffersSubmissionStepData;
			error?: string;
		}>({
			action: "postSecondaryOffersSubmissionStep",
			submissionType,
			submissionData,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function patchSecondaryOffersSubmission(
	submissionId: string,
	submissionData: object,
) {
	try {
		return await callClientAction<{
			data?: PatchSecondaryOffersSubmissionData;
			error?: string;
		}>({
			action: "patchSecondaryOffersSubmission",
			submissionId,
			submissionData,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function postSecondaryOffersSubmissionForm(
	submissionId: string,
	submissionData: object,
) {
	try {
		return await callClientAction<{
			data?: PostSecondaryOffersSubmissionFormData;
			error?: string;
		}>({
			action: "postSecondaryOffersSubmissionForm",
			submissionId,
			submissionData,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}

export async function getSecondaryOffersSubmission(submissionId: string) {
	try {
		return await callClientAction<{
			data?: GetSecondaryOffersSubmissionData;
			error?: string;
		}>({
			action: "getSecondaryOffersSubmission",
			submissionId,
		});
	} catch (error) {
		return { data: undefined, error: String(error) };
	}
}
